<template>
  <div class='work-flows-layout'>
    <div class='work-flows-layout__title'>
      {{ $t('workflows.layout.title') }}
    </div>
    <div class='work-flows-layout__tickets-section'>
      <div class='work-flows-layout__tickets-section__default'>
        <div class='work-flows-layout__tickets-section__default__title'>
          {{ $t('default.work.flows.title') }}
        </div>
        <div class='work-flows-layout__tickets-section__default__tickets'>
          <div class='work-flows-layout__tickets-section__default__tickets__ticket' v-for='(workFlow) in defaultWorkFlows' :key='workFlow.id'>
            <work-flow-ticket
              :work-flow='workFlow'
              @toggleInfoPopup='toggleInfoPopup'
              @toggleWorkflowTicketPopup='toggleWorkflowTicketPopup'
              @enabledChanged='enabledChanged'
            />
          </div>
        </div>
      </div>
      <div class='work-flows-layout__tickets-section__custom'>
        <div class='work-flows-layout__tickets-section__custom__title'>
          {{ $t('custom.work.flows.title') }}
        </div>
        <div class='work-flows-layout__tickets-section__custom__tickets'>
          <div class='work-flows-layout__tickets-section__custom__tickets__ticket' v-for='(workFlow) in customWorkFlows' :key='workFlow.id'>
            <work-flow-ticket
              :work-flow='workFlow'
              @enabledChanged='enabledChanged'
              @toggleInfoPopup='toggleInfoPopup'
              @toggleWorkflowTicketPopup='toggleWorkflowTicketPopup'
            />
          </div>
        </div>
      </div>
    </div>
    <div class='work-flows-layout__add-work-flow'>
      <custom-tooltip placement="right" popper-class="light-tooltip">
        <div slot='buttonContent'>
          <v-btn class='add-workflow-btn'
                 fab
                 large
                 :ripple="false"
                 @click='addWorkFlowPopup = !addWorkFlowPopup'
          >
            <v-icon>control_point</v-icon>
          </v-btn>
        </div>
        <div slot="content">
          <slot name="tooltip">
            <span> {{ $t('add.work.flow.tool.tip.label') }} </span>
          </slot>
        </div>
      </custom-tooltip>
    </div>
    <work-flow-graph-editor-popup
    :visible-popup='addWorkFlowPopup'
    @saveWorkflow='saveWorkflow'
    @closed='addWorkFlowPopup = !addWorkFlowPopup'
    />
    <message-popup
      v-if='currentWorkflowIdTicketForInfoPopup !== null'
      :data='allWorkflows.find(item => item.id === currentWorkflowIdTicketForInfoPopup).description || $t("no.description")'
      title='work.flow.information.popup.title'
      :visible-popup='textReviewPopup'
      okCaption='ok'
      @clicked='textReviewPopup = false'
    />
    <work-flow-diagram-popup
      v-if='currentWorkflowIdTicketForGraphPopup !== null'
      :visible-popup='workflowDiagramPopup'
      :work-flow-graph='allWorkflows.find(item => item.id === currentWorkflowIdTicketForGraphPopup).data'
      @closed='onDiagramClose'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WorkFlowTicket from '@/components/workflows/WorkFlowTicket.vue';
import MessagePopup from '@/components/popup/MessagePopup.vue';
import WorkFlowDiagramPopup from '@/components/popup/WorkFlowDiagramPopup.vue';
import WorkFlowGraphEditorPopup from '@/components/popup/WorkFlowGraphEditorPopup.vue';
import { ADD_WORKFLOW, LOAD_WORKFLOWS, UPDATE_WORKFLOWS } from '@/store/workflow/actionTypes';
import { workflows } from '@/services/workFlows';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'WorkFlowsLayout',
  components: {
    WorkFlowGraphEditorPopup,
    WorkFlowDiagramPopup,
    MessagePopup,
    WorkFlowTicket,
    CustomTooltip,
  },
  async created() {
    await this.$store.dispatch(LOAD_WORKFLOWS);
  },
  data() {
    return {
      workflowDiagramPopup: false,
      textReviewPopup: false,
      currentWorkflowIdTicketForGraphPopup: null,
      currentWorkflowIdTicketForInfoPopup: null,
      addWorkFlowPopup: false,
      basicWorkFlows: [
        {
          id: 0,
          description: 'Validate if a technology which is used is exploitable',
          performanceScore: 100,
          name: 'Vulnerable Technology',
          data: workflows.vulnerableTechnology,
          executionAmount: 4,
          domainsCovered: 10,
          default: false,
          status: 'enabled',
        },
        {
          id: 1,
          description: 'A new vulnerability will trigger a notification and assignment of task to relevant team member',
          performanceScore: 100,
          name: 'Vulnerability Discovered',
          data: workflows.vulnerabilityDiscovered,
          domainsCovered: 8,
          executionAmount: 15,
          default: false,
          status: 'disabled',
        },
        {
          id: 2,
          description: 'Validation of a new password leak of an employee against SaaS platforms and VPN services',
          performanceScore: 80,
          name: 'New Employee Leak Detected',
          data: workflows.newEmployeeLeakDetected,
          domainsCovered: 2,
          executionAmount: 12,
          default: false,
          status: 'enabled',
        },
        {
          id: 3,
          description: 'Verify of new subdomain is hijackable or can be accessed remotely',
          performanceScore: 20,
          name: 'New Subdomain Discovered',
          data: workflows.newSubdomainDiscovered,
          executionAmount: 8,
          domainsCovered: 5,
          default: true,
          status: 'disabled',
        },
        {
          id: 4,
          description: 'New Website identified during a discovery, validate that it doesn\'t contain high vulnerabilities.',
          performanceScore: 100,
          name: 'New Website Discovered',
          data: workflows.newWebsiteDiscovered,
          executionAmount: 10,
          domainsCovered: 15,
          default: true,
          status: 'enabled',
        },
        {
          id: 5,
          description: 'A new data leak will trigger a notification, automated verification, and removal of the leaked data',
          performanceScore: 50,
          name: 'Data Leak Detected',
          data: workflows.leaksDiscovered,
          domainsCovered: 5,
          executionAmount: 25,
          default: false,
          status: 'disabled',
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['workflows']),
    allWorkflows() {
      return [...this.workflows, ...this.basicWorkFlows];
    },
    defaultWorkFlows() {
      return this.allWorkflows.filter(workFlow => workFlow.default);
    },
    customWorkFlows() {
      return this.allWorkflows.filter(workFlow => !workFlow.default);
    },
  },
  methods: {
    async enabledChanged(id) {
      const relevantWorkflow = this.allWorkflows.find(item => item.id === id);
      relevantWorkflow.status = relevantWorkflow.status === 'enabled' ? 'disabled' : 'enabled';
      await this.$store.dispatch(UPDATE_WORKFLOWS, relevantWorkflow);
    },
    async saveWorkflow(workflowPayload) {
      await this.$store.dispatch(ADD_WORKFLOW, {
        ...workflowPayload,
      });
      this.addWorkFlowPopup = !this.addWorkFlowPopup;
    },
    onDiagramClose() {
      this.workflowDiagramPopup = false;
      this.currentWorkflowIdTicketForGraphPopup = null;
    },
    toggleInfoPopup(workflowId) {
      this.currentWorkflowIdTicketForInfoPopup = workflowId;
      if (!this.textReviewPopup) {
        this.textReviewPopup = true;
      }
    },
    // eslint-disable-next-line no-unused-vars
    toggleWorkflowTicketPopup(workflowId) {
      this.currentWorkflowIdTicketForGraphPopup = workflowId;
      if (!this.workflowDiagramPopup && this.allWorkflows.find(item => item.id === this.currentWorkflowIdTicketForGraphPopup).data) {
        this.workflowDiagramPopup = true;
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";

.work-flows-layout {
  display: flex;
  flex-direction: column;
  padding: $space-5 rem(86) $space-5 $space-8;

  &__title {
    padding-left: $space-8;
    padding-top: $space-8;
    margin-bottom: $space-8;
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-2xl;
    font-weight: bold;
    line-height: $line-height-tight;
    letter-spacing: $letter-spacing-normal;
  }

  &__tickets-section {
    padding-left: $space-8;
    display: flex;
    flex-direction: column;

    &__default {
      display: flex;
      flex-direction: column;

      &__title {
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-sm;
        text-transform: uppercase;
        margin-bottom: $space-4;
        letter-spacing: $letter-spacing-wide;
        line-height: $line-height-snug;
      }

      &__tickets {
        display: flex;
        flex-wrap: wrap;

        &__ticket {
          margin-right: $space-8;
          margin-bottom: $space-8;
        }
      }
    }

    &__custom {
      display: flex;
      flex-direction: column;

      &__ticket {
        margin-right: $space-8;
        margin-bottom: $space-8;
      }

      &__tickets {
        display: flex;
        flex-wrap: wrap;

        &__ticket {
          margin-right: $space-8;
          margin-bottom: $space-8;
        }
      }

      &__title {
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-sm;
        text-transform: uppercase;
        margin-bottom: $space-4;
        letter-spacing: $letter-spacing-wide;
        line-height: $line-height-snug;
      }
    }
  }

  &__add-work-flow {
    z-index: $z-sticky;
    position: fixed;
    bottom: $space-6;

    .v-btn--round.v-btn--round {
      border-radius: $radius-full;
      color: $add-workflow-btn-color;
      font-size: $font-size-5xl;
    }

    .add-workflow-btn.add-workflow-btn {
      background-color: $action-button-background-color;
      box-shadow: none;
      font-family: $font-family-medium;
      text-align: center;
      min-width: $header-height;
      max-width: $header-height;
      min-height: $header-height;
      max-height: $header-height;
      color: $action-button-text-color;
      margin-top: $space-0-5;

      .v-icon {
        font-size: rem(56);
      }

      &:hover {
        cursor: pointer;
        border: $action-button-hover-border;
        background-color: $action-button-hover-background-color;
      }

      &:active {
        border: $action-button-active-border;
        background-color: $action-button-hover-background-color;
      }

      &:before {
        color: transparent;
      }
    }
  }
}
</style>
